/**
 * List Management - Shared utilities for list pages (bookmarks, friends, etc.)
 * Provides common edit mode toggle and item removal functionality
 *
 * @author InterPals Development Team
 * @version 1.0.0
 */

(function() {
    'use strict';

    // Helper to get translation (falls back to key if i18n not available)
    function t(key) {
        return window.t ? window.t(key) : key;
    }

    /**
     * Initialize edit mode toggle for a list page
     * @param {Object} options - Configuration options
     * @param {string} options.deleteButtonClass - CSS class for delete buttons (e.g., 'bookmark-delete-btn')
     */
    function initEditMode(options) {
        var deleteButtonClass = options.deleteButtonClass;
        var editToggle = document.getElementById('edit-mode-toggle');
        if (!editToggle) return;

        editToggle.addEventListener('click', function() {
            var isActive = editToggle.dataset.editActive === 'true';
            var newState = !isActive;

            // Update button state
            editToggle.dataset.editActive = newState;
            var textSpan = editToggle.querySelector('[data-edit-text]');
            var icon = editToggle.querySelector('i');

            if (newState) {
                textSpan.textContent = t('common.done');
                editToggle.classList.remove('text-gray-700', 'bg-white', 'border-gray-300', 'hover:bg-gray-50');
                editToggle.classList.add('bg-sky-500', 'text-white', 'border-transparent', 'hover:bg-sky-600');
                icon.classList.remove('fa-edit');
                icon.classList.add('fa-check');
            } else {
                textSpan.textContent = t('common.edit');
                editToggle.classList.remove('bg-sky-500', 'text-white', 'border-transparent', 'hover:bg-sky-600');
                editToggle.classList.add('text-gray-700', 'bg-white', 'border-gray-300', 'hover:bg-gray-50');
                icon.classList.remove('fa-check');
                icon.classList.add('fa-edit');
            }

            // Update container data attribute for CSS selector
            var container = document.querySelector('[data-edit-mode]');
            if (container) {
                container.dataset.editMode = newState;
            }

            // Show/hide delete buttons on mobile
            var deleteButtons = document.querySelectorAll('.' + deleteButtonClass);
            deleteButtons.forEach(function(button) {
                if (window.innerWidth < 768) {
                    if (newState) {
                        button.classList.remove('hidden');
                        button.classList.add('block');
                    } else {
                        button.classList.add('hidden');
                        button.classList.remove('block');
                    }
                }
            });
        });
    }

    /**
     * Remove an item from a list with optimistic UI
     * @param {Object} options - Configuration options
     * @param {HTMLElement} options.button - The delete button that was clicked
     * @param {HTMLElement} options.card - The card element to remove
     * @param {string} options.url - The endpoint URL
     * @param {Object} options.formData - Additional form data fields (key-value pairs)
     * @param {string} options.errorMessage - Default error message
     * @param {Function} options.onSuccess - Callback after successful removal (optional)
     * @param {string} options.logPrefix - Prefix for console error logs
     */
    async function removeItem(options) {
        var button = options.button;
        var card = options.card;
        var url = options.url;
        var formDataFields = options.formData || {};
        var defaultErrorMessage = options.errorMessage || t('common.remove_item_error');
        var onSuccess = options.onSuccess;
        var logPrefix = options.logPrefix || 'Item removal';

        // Optimistic UI: hide card immediately
        if (card) {
            card.style.transition = 'all 0.3s ease-out';
            card.style.transform = 'scale(0.8)';
            card.style.opacity = '0';
        }

        // Disable button to prevent double-clicks
        button.disabled = true;

        try {
            // Create form data
            var formData = new FormData();
            Object.keys(formDataFields).forEach(function(key) {
                formData.append(key, formDataFields[key]);
            });

            // Add CSRF token if available
            var csrfInput = document.querySelector('input[name="csrf_token"]');
            if (csrfInput) {
                formData.append('csrf_token', csrfInput.value);
            }

            // Make request
            var response = await fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });

            var errorMessage = defaultErrorMessage;
            var data;

            try {
                data = await response.json();
            } catch (parseError) {
                // Response is not valid JSON
                throw new Error(errorMessage);
            }

            if (!response.ok || !data.success) {
                errorMessage = data.error || errorMessage;
                throw new Error(errorMessage);
            }

            // Success - remove card permanently
            if (card) {
                setTimeout(function() {
                    card.remove();
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 300);
            }

        } catch (error) {
            // Restore card on error
            if (card) {
                card.style.transform = '';
                card.style.opacity = '';
            }
            button.disabled = false;

            // Show user-friendly error message
            console.error(logPrefix + ' failed:', error);
            alert(error.message || defaultErrorMessage);
        }
    }

    /**
     * Show confirmation modal and handle item removal
     * @param {Object} options - Configuration options
     * @param {string} options.title - Modal title
     * @param {string} options.message - Modal message
     * @param {string} options.confirmText - Confirm button text (default: 'Remove')
     * @param {Function} options.onConfirm - Callback when confirmed
     * @param {string} options.fallbackMessage - Message for native confirm fallback
     */
    function confirmAndRemove(options) {
        var title = options.title;
        var message = options.message;
        var confirmText = options.confirmText || t('common.remove');
        var onConfirm = options.onConfirm;
        var fallbackMessage = options.fallbackMessage || message;

        if (window.ConfirmModal) {
            ConfirmModal.show({
                title: title,
                message: message,
                confirmText: confirmText,
                type: 'danger',
                onConfirm: onConfirm
            });
        } else {
            // Fallback to native confirm
            if (confirm(fallbackMessage)) {
                onConfirm();
            }
        }
    }

    // Export to window for use by other modules
    window.ListManagement = {
        initEditMode: initEditMode,
        removeItem: removeItem,
        confirmAndRemove: confirmAndRemove
    };

})();
